import { useState, useEffect } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Loader2, Send, AlertCircle, Users, Check, Plus, X } from "lucide-react";

/**
 * “Send to client” on a delivery round — one dialog, both rails.
 *
 * The visualization rail and the VEXOS rail ask for the same three things: a link, an optional line from the PM,
 * and confirmation of who it reaches. They differ only in wording, so they
 * share a component rather than drifting into two dialogs that gradually stop
 * agreeing about what “Send” means.
 *
 * Two things this deliberately shows before you can press the button:
 *
 *   * THE RECIPIENT LIST, in full, one checkbox per person. “Send to client”
 *     quietly meaning five people — the client plus everyone they added
 *     themselves — is the kind of surprise you only get to spring once, and the
 *     client often says who should see a round ("just me for the draft", "copy
 *     the architect on the final"). Someone not on the list yet is added with
 *     “+ Add someone” and ticked. Manager meeting, 2026-11-02.
 *     Or TYPED: the To and CC boxes take any address on the spot, for this send
 *     only unless "save to the client's team" is ticked. The CC line rides on
 *     one email, so a CC'd person gets one copy. COO request, 2026-11-02.
 *   * THE SEND MODE. The tracker's email module ships paused, and a Send button
 *     that flashes green while nothing leaves the building is worse than one
 *     that refuses. Paused and test modes say so here, in advance.
 *
 * `multiLink` (visualization) asks for one link per deliverable — orbit and
 * keyframes, stills, 360s, often split by unit — each with a type and an
 * optional description. The VEXOS rail sends one platform link.
 *
 * On a phone the body scrolls and the header and Send button stay put — the
 * dialog used to grow past the bottom of the screen with Send below the fold.
 */

// The backend's own rule (project_tracker._EMAIL_RE) and caps, so the box
// refuses what the server would refuse, while the PM is still typing.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MAX_TYPED = 10;

/** Free-text addresses as chips. Comma, semicolon, space, Enter or leaving the
 * box turns what was typed into chips; a malformed one stays in the box with
 * the reason under it instead of being dropped. `accept(email)` lets the
 * parent route an address elsewhere (To ticks someone already on the project). */
function AddressBox({ label, values, onChange, accept, placeholder, testId, onPending }) {
  const [draft, setDraftState] = useState("");
  const [error, setError] = useState("");
  // The parent hears when something is still sitting in the box, so Send can
  // refuse rather than quietly go out without the address the PM mistyped.
  const setDraft = (v) => { setDraftState(v); onPending?.(Boolean(v.trim())); };

  const commit = (text) => {
    const parts = (text ?? draft).split(/[,;\s]+/).map((p) => p.replace(/^<|>$/g, "").trim()).filter(Boolean);
    if (!parts.length) { setDraft(""); return; }
    const next = [...values];
    const bad = [];
    let message = "";
    for (const raw of parts) {
      const email = raw.toLowerCase();
      if (!EMAIL_RE.test(email)) { bad.push(raw); continue; }
      if (next.includes(email)) continue;
      if (accept && accept(email) === false) continue;
      if (next.length >= MAX_TYPED) { message = `Up to ${MAX_TYPED} addresses here.`; break; }
      next.push(email);
    }
    if (next.length !== values.length) onChange(next);
    setDraft(bad.join(", "));
    setError(bad.length ? `“${bad[0]}” isn't a valid email address.` : message);
  };

  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div
        className={`mt-1 flex flex-wrap items-center gap-1.5 rounded-lg border bg-background px-2 py-1.5 ${error ? "border-red-400" : "border-input"}`}
        data-testid={testId}
      >
        {values.map((email) => (
          <span key={email} className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">
            <span className="truncate">{email}</span>
            <button
              type="button"
              onClick={() => { onChange(values.filter((v) => v !== email)); setError(""); }}
              className="shrink-0 text-muted-foreground hover:text-foreground"
              aria-label={`Remove ${email}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="off"
          value={draft}
          onChange={(e) => {
            const v = e.target.value;
            if (/[,;\s]$/.test(v)) commit(v); else { setDraft(v); if (error) setError(""); }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Backspace" && !draft && values.length) onChange(values.slice(0, -1));
          }}
          onBlur={() => commit()}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (/[,;\s]/.test(text.trim())) { e.preventDefault(); commit(`${draft}${text}`); }
          }}
          placeholder={values.length ? "" : placeholder}
          className="min-w-[8rem] flex-1 bg-transparent py-0.5 text-base sm:text-sm outline-none placeholder:text-muted-foreground/70"
          aria-label={label}
        />
      </div>
      {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
    </div>
  );
}

export default function SendDeliveryDialog({
  open,
  onClose,
  onSend,
  roundLabel,
  roundTitle,
  linkLabel = "Review link",
  linkPlaceholder = "https://…",
  linkHint,
  initialUrl = "",
  initialLinks,
  initialNote = "",
  recipients = [],
  emailMode = "paused",
  alreadySentText,
  programId,
  multiLink = false,
  linkKinds = [],
  teamRoles = [],
  pickSessions = true,
  onRecipientsChange,
}) {
  const [links, setLinks] = useState(() => {
    const start = (initialLinks && initialLinks.length)
      ? initialLinks.map((l) => ({ url: l.url || "", kind: l.kind || "", label: l.label || "" }))
      : [{ url: initialUrl, kind: "", label: "" }];
    return start;
  });
  const url = links[0]?.url || "";
  const setUrl = (value) => setLinks((ls) => [{ ...(ls[0] || { kind: "", label: "" }), url: value }, ...ls.slice(1)]);
  const [activeRow, setActiveRow] = useState(0);
  const [note, setNote] = useState(initialNote);
  const [people, setPeople] = useState(recipients);
  const [checked, setChecked] = useState(() => new Set(recipients.map((r) => r.email)));
  const [adding, setAdding] = useState(false);
  const [newPerson, setNewPerson] = useState({ name: "", email: "", role: "" });
  const [savingPerson, setSavingPerson] = useState(false);
  const [toTyped, setToTyped] = useState([]);
  const [ccTyped, setCcTyped] = useState([]);
  // Off by default: a typed address is for this send unless the PM says keep it.
  const [saveTyped, setSaveTyped] = useState(false);
  const [pendingBox, setPendingBox] = useState({ to: false, cc: false });
  const [sending, setSending] = useState(false);

  const updateLink = (i, patch) => setLinks((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const addLinkRow = () => {
    setLinks((ls) => [...ls, { url: "", kind: "", label: "" }]);
    setActiveRow(links.length);
  };
  const removeLinkRow = (i) => {
    setLinks((ls) => (ls.length === 1 ? [{ url: "", kind: "", label: "" }] : ls.filter((_, j) => j !== i)));
    setActiveRow(0);
  };

  /* This project's review sessions, so the link can be PICKED.
   *
   * Sending V1 used to mean leaving this page, opening Review Studio, finding
   * the session, copying its client link, coming back and pasting it — with a
   * free-text box as the only guard against pasting the internal URL, last
   * project's link, or a truncated one. The sessions are keyed by program and
   * already carry their own client token, so the list is right here. Paste
   * stays available: not every delivery goes through Review Studio. */
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (!open || !programId || !pickSessions) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await apiClient.get("/review-studio/sessions", { params: { program_id: programId } });
        if (alive) setSessions((res.data || []).filter((x) => x.client_token));
      } catch {
        // A picker that can't load is a picker that isn't offered. Paste still works.
        if (alive) setSessions([]);
      }
    })();
    return () => { alive = false; };
  }, [open, programId, pickSessions]);

  const sessionUrl = (sess) => `${window.location.origin}/review/${sess.client_token}`;

  // Picking a session fills the row being edited — or, when that row already
  // holds a link, starts a new row, so picking three sessions gives three links.
  const pickSession = (href) => {
    if (!multiLink) { setUrl(href); return; }
    const row = links[activeRow];
    if (row && !row.url.trim()) {
      updateLink(activeRow, { url: href });
    } else if (!links.some((l) => l.url.trim() === href)) {
      setLinks((ls) => [...ls, { url: href, kind: "", label: "" }]);
      setActiveRow(links.length);
    }
  };
  const isPicked = (href) => links.some((l) => l.url.trim() === href);

  const primary = people.find((p) => p.primary) || null;
  const willReach = people.filter((p) => checked.has(p.email));
  const toggle = (email) => setChecked((c) => {
    const next = new Set(c);
    if (next.has(email)) next.delete(email); else next.add(email);
    return next;
  });

  // Typing someone who is already on the project just ticks them.
  const knownEmails = new Set(people.map((p) => p.email));
  const acceptTo = (email) => {
    if (!knownEmails.has(email)) return true;
    setChecked((c) => new Set([...c, email]));
    return false;
  };
  const toEmails = new Set([...willReach.map((p) => p.email), ...toTyped]);
  // Someone in To never gets a second copy as a CC — the server holds the same line.
  const ccToSend = ccTyped.filter((e) => !toEmails.has(e));
  const newTyped = [...new Set([...toTyped, ...ccToSend])].filter((e) => !knownEmails.has(e));
  const toCount = toEmails.size;

  const addPerson = async () => {
    const name = newPerson.name.trim();
    const email = newPerson.email.trim();
    if (!name || !email) {
      toast.error("Add their name and email.");
      return;
    }
    setSavingPerson(true);
    try {
      const res = await apiClient.post(`/project-tracker/program/${programId}/team`, {
        name, email, role: newPerson.role || null,
      });
      const list = res.data.delivery_recipients || [];
      setPeople(list);
      setChecked((c) => new Set([...c, email.toLowerCase()]));
      setNewPerson({ name: "", email: "", role: "" });
      setAdding(false);
      onRecipientsChange?.(list);
      toast.success(`${name} added to the client's team.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add them.");
    } finally {
      setSavingPerson(false);
    }
  };

  const submit = async () => {
    const filled = links
      .map((l) => ({ url: l.url.trim(), kind: l.kind || null, label: l.label.trim() || null }))
      .filter((l) => l.url || l.label);
    if (!filled.length) {
      toast.error("Paste the link the client should open.");
      return;
    }
    if (filled.some((l) => !/^https?:\/\/\S+$/i.test(l.url))) {
      toast.error("Each link needs to be the full address, starting with https://");
      return;
    }
    if (!primary) {
      toast.error("This project has no client email on its tracker yet — add one first.");
      return;
    }
    if (pendingBox.to || pendingBox.cc) {
      toast.error(`Fix or remove the address still in the ${pendingBox.to ? "To" : "CC"} box.`);
      return;
    }
    if (!toCount) {
      toast.error("Tick or type at least one person to send this to.");
      return;
    }
    setSending(true);
    try {
      await onSend({
        url: filled[0].url,
        ...(multiLink ? { links: filled } : {}),
        note: note.trim(),
        recipients: willReach.map((p) => p.email),
        to: toTyped,
        cc: ccToSend,
        save_to_team: saveTyped ? newTyped : [],
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !sending) onClose(); }}>
      <DialogContent className="max-w-lg w-[calc(100vw-1.5rem)] sm:w-full max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] flex flex-col gap-4 p-5 sm:p-7">
        <DialogHeader className="shrink-0 pr-6 text-left">
          <DialogTitle>Send {roundLabel} to the client</DialogTitle>
          <DialogDescription>
            {roundTitle
              ? `${roundTitle}. `
              : ""}
            They'll get an email with a button that opens this delivery inside their
            project dashboard — not the raw link.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain -mx-5 px-5 sm:-mx-7 sm:px-7 space-y-4 py-1">
          {sessions.length > 0 && (
            <div>
              <Label className="text-xs text-muted-foreground">Pick this project's review session</Label>
              <div className="mt-1.5 rounded-lg border divide-y">
                {sessions.slice(0, 6).map((sess) => {
                  const href = sessionUrl(sess);
                  const chosen = isPicked(href);
                  return (
                    <button
                      key={sess.id}
                      type="button"
                      onClick={() => pickSession(href)}
                      className="w-full flex items-center gap-3 p-2.5 text-left hover:bg-muted/40"
                    >
                      <span className={`w-3.5 h-3.5 rounded-full border shrink-0 flex items-center justify-center ${chosen ? "bg-foreground border-foreground" : "border-input"}`}>
                        {chosen && <Check className="w-2.5 h-2.5 text-background" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm truncate">{sess.title || "Untitled session"}</span>
                        <span className="block text-xs text-muted-foreground">
                          {sess.asset_count || 0} asset{sess.asset_count === 1 ? "" : "s"}
                          {sess.comment_count ? ` · ${sess.comment_count} comment${sess.comment_count === 1 ? "" : "s"}` : ""}
                          {sess.status ? ` · ${sess.status}` : ""}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                {multiLink
                  ? "Each one you pick becomes a link below. Or paste your own."
                  : "Or paste a different link below."}
              </p>
            </div>
          )}

          {multiLink ? (
            <div>
              <Label className="text-xs text-muted-foreground">{linkLabel}s in this delivery</Label>
              <div className="mt-1.5 space-y-2">
                {links.map((l, i) => (
                  <div key={i} className="rounded-lg border p-2 space-y-2" onFocusCapture={() => setActiveRow(i)}>
                    <div className="flex gap-2">
                      <select
                        value={l.kind}
                        onChange={(e) => updateLink(i, { kind: e.target.value })}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm shrink-0"
                        aria-label="What this link is"
                        data-testid={`delivery-link-kind-${i}`}
                      >
                        <option value="">Type…</option>
                        {linkKinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                      </select>
                      <Input
                        value={l.label}
                        onChange={(e) => updateLink(i, { label: e.target.value })}
                        placeholder="Description, e.g. Unit A (optional)"
                        maxLength={80}
                      />
                      <Button type="button" variant="ghost" size="icon" className="shrink-0"
                        onClick={() => removeLinkRow(i)} aria-label="Remove this link">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <Input
                      value={l.url}
                      onChange={(e) => updateLink(i, { url: e.target.value })}
                      placeholder={linkPlaceholder}
                      data-testid={i === 0 ? "delivery-link-input" : `delivery-link-input-${i}`}
                    />
                  </div>
                ))}
              </div>
              <Button type="button" variant="ghost" size="sm" className="mt-1.5 h-8 px-2" onClick={addLinkRow}
                data-testid="delivery-add-link">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add another link
              </Button>
              {linkHint && <p className="text-xs text-muted-foreground mt-1">{linkHint}</p>}
            </div>
          ) : (
            <div>
              <Label className="text-xs text-muted-foreground">{linkLabel}</Label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={linkPlaceholder}
                className="mt-1.5"
                data-testid="delivery-link-input"
              />
              {linkHint && <p className="text-xs text-muted-foreground mt-1.5">{linkHint}</p>}
            </div>
          )}

          <div>
            <Label className="text-xs text-muted-foreground">A line for the client (optional)</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Anything you want them to look at first."
              className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              Shows in the email and under the round on their dashboard.
            </p>
          </div>

          {/* Exactly who this reaches, one box per person. */}
          <div className="rounded-lg border bg-muted/30 p-3">
            <p className="text-xs font-medium flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Goes to {willReach.length} of {people.length} on the project
              {toTyped.length > 0 && ` + ${toTyped.length} typed`}
              {ccToSend.length > 0 && ` · ${ccToSend.length} CC`}
            </p>
            {primary ? (
              <ul className="mt-2 space-y-1.5">
                {people.map((p) => (
                  <li key={p.email}>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked.has(p.email)}
                        onChange={() => toggle(p.email)}
                        className="rounded border-input"
                        data-testid={`delivery-recipient-${p.email}`}
                      />
                      <span className="min-w-0 truncate">
                        {p.name ? <>{p.name} <span className="text-muted-foreground">· {p.email}</span></> : p.email}
                        {p.role_label && <span className="text-muted-foreground opacity-70"> · {p.role_label}</span>}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-amber-700 mt-1.5">
                No client email on this tracker yet — add one in Tracker settings before sending.
              </p>
            )}
            {primary && programId && (adding ? (
              <div className="mt-3 space-y-2 border-t pt-3">
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input value={newPerson.name} placeholder="Name"
                    onChange={(e) => setNewPerson((n) => ({ ...n, name: e.target.value }))} />
                  <Input value={newPerson.email} placeholder="Email" type="email"
                    onChange={(e) => setNewPerson((n) => ({ ...n, email: e.target.value }))} />
                </div>
                {teamRoles.length > 0 && (
                  <select
                    value={newPerson.role}
                    onChange={(e) => setNewPerson((n) => ({ ...n, role: e.target.value }))}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    aria-label="Their role on the project"
                  >
                    <option value="">Role on the project (optional)</option>
                    {teamRoles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                )}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(false)} disabled={savingPerson}>
                    Cancel
                  </Button>
                  <Button type="button" size="sm" className="rounded-full" onClick={addPerson} disabled={savingPerson}>
                    {savingPerson && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                    Add to the team
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  They join the client's team on the dashboard. No separate invite — this delivery email is their way in.
                </p>
              </div>
            ) : (
              <Button type="button" variant="ghost" size="sm" className="mt-2 h-7 px-2 text-xs"
                onClick={() => setAdding(true)} data-testid="delivery-add-person">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add someone
              </Button>
            ))}

            {/* Anyone else, typed on the spot — for this send unless saved. */}
            {primary && (
              <div className="mt-3 space-y-2.5 border-t pt-3">
                <AddressBox
                  label="To — anyone else"
                  values={toTyped}
                  onChange={setToTyped}
                  accept={acceptTo}
                  onPending={(v) => setPendingBox((p) => ({ ...p, to: v }))}
                  placeholder="name@company.com"
                  testId="delivery-to-input"
                />
                <AddressBox
                  label="CC"
                  values={ccTyped}
                  onChange={setCcTyped}
                  onPending={(v) => setPendingBox((p) => ({ ...p, cc: v }))}
                  placeholder="Separate several with commas"
                  testId="delivery-cc-input"
                />
                {ccTyped.length > ccToSend.length && (
                  <p className="text-[11px] text-muted-foreground">
                    {ccTyped.length - ccToSend.length === 1 ? "One CC is" : "Some CCs are"} already
                    getting it as a To, so won't be copied twice.
                  </p>
                )}
                {newTyped.length > 0 && (
                  <label className="flex items-start gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={saveTyped}
                      onChange={(e) => setSaveTyped(e.target.checked)}
                      className="rounded border-input mt-0.5"
                      data-testid="delivery-save-typed"
                    />
                    <span>
                      Save {newTyped.length === 1 ? "this address" : `these ${newTyped.length} addresses`} to
                      the client's team
                      <span className="block text-muted-foreground">
                        {saveTyped
                          ? "They'll be listed on the project from now on, and filed in Yellow Pages."
                          : "Left off, they get this email only."}
                      </span>
                    </span>
                  </label>
                )}
              </div>
            )}
          </div>

          {emailMode !== "live" && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
              <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">
                {emailMode === "test"
                  ? "Client emails are in test mode — this will be redirected to the internal test address, not the client."
                  : "Client emails are paused, so nothing will actually be sent. The link and the delivered date are still recorded on their dashboard."}
              </p>
            </div>
          )}

          {alreadySentText && (
            <p className="text-xs text-muted-foreground">
              Last sent {alreadySentText}. Sending again re-notifies everyone ticked above.
            </p>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={submit} disabled={sending || !primary || !toCount} className="rounded-full" data-testid="delivery-send-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send to client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
